/**
 * AbilityProvider / useAbility — CASL ability for the current user.
 *
 * Usage:
 *   const ability = useAbility();
 *   if (ability.can("update", "Invoice")) { … }
 *
 * Or with the bound <Can> component:
 *   import { Can } from "@/contexts/AbilityContext";
 *   <Can I="delete" a="Lead">{() => <Button>Delete</Button>}</Can>
 */
import { createContext, useContext, useEffect, useMemo, useState } from "react";
import { createContextualCan } from "@casl/react";
import { createMongoAbility } from "@casl/ability";
import { useAuth } from "@/contexts/AuthContext";
import { buildAbilityFor, type AppAbility } from "@/services/abilitiesService";

const EMPTY_ABILITY: AppAbility = createMongoAbility([]);

interface AbilityCtx {
  ability: AppAbility;
  loading: boolean;
  refresh: () => Promise<void>;
}

const Ctx = createContext<AbilityCtx>({
  ability: EMPTY_ABILITY,
  loading: true,
  refresh: async () => {},
});

export const AbilityProvider = ({ children }: { children: React.ReactNode }) => {
  const { user } = useAuth();
  const [ability, setAbility] = useState<AppAbility>(EMPTY_ABILITY);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    if (!user?.id) {
      setAbility(EMPTY_ABILITY);
      setLoading(false);
      return;
    }
    try {
      const next = await buildAbilityFor(user.id);
      setAbility(next);
    } catch (err) {
      console.error("[AbilityProvider] failed to load abilities", err);
      setAbility(EMPTY_ABILITY);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    setLoading(true);
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id]);

  const value = useMemo<AbilityCtx>(
    () => ({ ability, loading, refresh: load }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [ability, loading],
  );

  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
};

export const useAbility = (): AppAbility => useContext(Ctx).ability;
export const useAbilityContext = () => useContext(Ctx);

// Bound <Can I="update" a="Invoice"> component
const AbilityReactContext = createContext<AppAbility>(EMPTY_ABILITY);
export const Can = createContextualCan(AbilityReactContext.Consumer);

/** Bridge — feeds the React context that <Can> consumes. */
export const AbilityBridge = ({ children }: { children: React.ReactNode }) => {
  const ability = useAbility();
  return (
    <AbilityReactContext.Provider value={ability}>
      {children}
    </AbilityReactContext.Provider>
  );
};
